/**
 * ManagedSqlDbStatusBadge Component
 *
 * Displays a status badge for managed SQL database status with appropriate colors.
 */

import React from 'react';
import { ManagedSqlDbStatus } from '../../../types/managed-sql-db.types';

interface ManagedSqlDbStatusBadgeProps {
  status: ManagedSqlDbStatus;
  statusMessage?: string;
}

/**
 * Status configuration with labels and colors
 */
const statusConfig: Record<
  ManagedSqlDbStatus,
  { label: string; bgColor: string; textColor: string }
> = {
  [ManagedSqlDbStatus.CREATING]: {
    label: 'Creating',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
  },
  [ManagedSqlDbStatus.AVAILABLE]: {
    label: 'Available',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
  },
  [ManagedSqlDbStatus.UPDATING]: {
    label: 'Updating',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
  },
  [ManagedSqlDbStatus.DELETING]: {
    label: 'Deleting',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800',
  },
  [ManagedSqlDbStatus.FAILED]: {
    label: 'Failed',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
  },
};

/**
 * ManagedSqlDbStatusBadge component
 */
export const ManagedSqlDbStatusBadge: React.FC<ManagedSqlDbStatusBadgeProps> = ({
  status,
  statusMessage,
}) => {
  const config = statusConfig[status] || {
    label: status,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      title={statusMessage}
    >
      {status === ManagedSqlDbStatus.CREATING && (
        <svg
          className="animate-spin -ml-0.5 mr-1.5 h-3 w-3"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {config.label}
    </span>
  );
};
